// Locale-aware number / currency formatters used across every financial screen
// and export. API mirrors backend/src/pdf/format.js so a value formatted in the
// browser matches what the same value looks like in a generated PDF or XLSX.
//
//   fmtCurrency(value, currency?)         → "60.000 kr." style, 0 decimals
//   fmtCurrencyLine(value, currency?)     → "60.000,00 kr." style, 2 decimals
//   fmtCurrencyCompact(value, currency?)  → "60K kr." style, ≤1 decimal
//   fmtVariance(value, currency?)         → signed "+60.000 kr." / "−500,00 kr."
//   fmtNumber(value, opts?)               → locale-aware non-currency number
//   setNumberLocale(locale)               → wired from i18n provider in App.jsx
//   resolveBcp47(lang)                    → 'da' → 'da-DK' etc. (shared with backend)
//
// All helpers return '—' for null / undefined / '' / NaN so call sites never
// have to defensively check before rendering.

import { useSettingsStore } from '../store/settingsStore.js'

const DEFAULT_LOCALE  = 'da-DK'
const DEFAULT_CURRENCY = 'DKK'

let _numberLocale = DEFAULT_LOCALE
export function setNumberLocale(locale) {
  _numberLocale = resolveBcp47(locale) || DEFAULT_LOCALE
}

// Mirror backend/src/pdf/format.js::resolveBcp47 so frontend and PDF engine
// agree on grouping/decimal separators per locale.
export function resolveBcp47(locale) {
  if (!locale) return null
  const tag = String(locale).toLowerCase()
  switch (tag) {
    case 'en':    return 'en-GB'
    case 'da':    return 'da-DK'
    case 'de':    return 'de-DE'
    case 'no':    return 'nb-NO'
    case 'nb':    return 'nb-NO'
    case 'sv':    return 'sv-SE'
    default:      return tag.includes('-') ? tag : null
  }
}

function _locale(override) {
  return resolveBcp47(override) || _numberLocale || DEFAULT_LOCALE
}

function _currency(override) {
  if (override) return override
  try {
    return useSettingsStore.getState().currency || DEFAULT_CURRENCY
  } catch {
    return DEFAULT_CURRENCY
  }
}

function _toNum(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

// 0 decimals — for stat-card KPIs, group subtotals, grand totals.
/**
 * @param {number|string|null|undefined} value
 * @param {string|null} [currency]
 * @param {string|null} [locale]
 * @returns {string}
 */
export function fmtCurrency(value, currency = null, locale = null) {
  const n = _toNum(value)
  if (n === null) return '—'
  const curr = _currency(currency)
  try {
    return new Intl.NumberFormat(_locale(locale), {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n)
  } catch {
    return `${curr} ${n.toFixed(0)}`.trim()
  }
}

// 2 decimals — for line-item rows (petty cash, per-diem, invoices, marketplace).
export function fmtCurrencyLine(value, currency = null, locale = null) {
  const n = _toNum(value)
  if (n === null) return '—'
  const curr = _currency(currency)
  try {
    return new Intl.NumberFormat(_locale(locale), {
      style: 'currency',
      currency: curr,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n)
  } catch {
    return `${curr} ${n.toFixed(2)}`.trim()
  }
}

// Compact form for very large dashboards — "60K kr.", "1,2M kr.".
export function fmtCurrencyCompact(value, currency = null, locale = null) {
  const n = _toNum(value)
  if (n === null) return '—'
  const curr = _currency(currency)
  try {
    return new Intl.NumberFormat(_locale(locale), {
      style: 'currency',
      currency: curr,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(n)
  } catch {
    return `${curr} ${n.toFixed(1)}`.trim()
  }
}

// Signed currency for budget variance. Uses unambiguous typographic minus
// (U+2212) so screen readers and exports never confuse it with a hyphen.
export function fmtVariance(value, currency = null, locale = null) {
  const n = _toNum(value)
  if (n === null) return '—'
  const abs = fmtCurrency(Math.abs(n), currency, locale)
  if (n > 0) return `+${abs}`
  if (n < 0) return `−${abs}`
  return abs
}

// Same idea for 2-decimal line items.
export function fmtVarianceLine(value, currency = null, locale = null) {
  const n = _toNum(value)
  if (n === null) return '—'
  const abs = fmtCurrencyLine(Math.abs(n), currency, locale)
  if (n > 0) return `+${abs}`
  if (n < 0) return `−${abs}`
  return abs
}

// Non-currency locale-aware number. Used for percentages, counts, ratios.
export function fmtNumber(value, opts = {}, locale = null) {
  const n = _toNum(value)
  if (n === null) return '—'
  const { minimumFractionDigits = 0, maximumFractionDigits = 2, ...rest } = opts
  try {
    return new Intl.NumberFormat(_locale(locale), {
      minimumFractionDigits,
      maximumFractionDigits,
      ...rest,
    }).format(n)
  } catch {
    return n.toFixed(maximumFractionDigits)
  }
}

// Percentage with one decimal — "12,5 %" in da-DK, "12.5%" in en-GB.
export function fmtPercent(value, opts = {}, locale = null) {
  const n = _toNum(value)
  if (n === null) return '—'
  const { minimumFractionDigits = 0, maximumFractionDigits = 1, ...rest } = opts
  try {
    return new Intl.NumberFormat(_locale(locale), {
      style: 'percent',
      minimumFractionDigits,
      maximumFractionDigits,
      ...rest,
    }).format(n)
  } catch {
    return `${(n * 100).toFixed(maximumFractionDigits)}%`
  }
}
